import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { services } from '../mock';
import ServiceTile from '../components/sections/ServiceTile';
import AvailabilityChecker from '../components/sections/AvailabilityChecker';
import PageMeta from '../components/sections/PageMeta';
import { LOCAL_BUSINESS_SCHEMA } from '../lib/localBusinessSchema';

const HeroSection = () => (
  <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20 px-4 sm:px-6 lg:px-8">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-8">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
            Bring-A-Nurse —{' '}
            <span className="text-blue-600">Here to Help You Navigate Care with Confidence</span>
          </h1>

          <p className="text-xl text-gray-600 leading-relaxed">
            Doctor visits and medical discussions can feel overwhelming. At Bring-A-Nurse Senior Companion PLLC, a compassionate, experienced nurse accompanies you to your appointments, offering one-on-one guidance, comfort, and support for seniors and their families.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-coral-500 hover:bg-coral-600 text-white text-lg px-8 py-6 w-full sm:w-auto">
                Book Your Consultation Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>

          <p className="text-sm text-gray-500">
            Serving Fuquay Varina, Holly Springs, Apex, Cary & surrounding areas
          </p>
        </div>

        <div className="relative">
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1758691462858-f1286e5daf40"
              alt="Nurse with senior patient"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-6 hidden sm:block">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 rounded-full p-3">
                <Star className="w-6 h-6 text-blue-600 fill-blue-600" />
              </div>
              <div>
                <p className="font-bold text-gray-900">Licensed RN</p>
                <p className="text-sm text-gray-600">Fully insured</p>
                <p className="text-sm text-gray-600">Professional Care</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ServicesSection = () => (
  <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
          How We Support You
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Bring-A-Nurse helps you navigate healthcare with clarity, confidence, and compassionate guidance. When appointments feel overwhelming or information feels confusing, we step in as your trusted advocate — helping you understand your options, ask the right questions, and feel more prepared every step of the way.
          <br /><br />
          Our focus is simple: reduce the noise, strengthen communication, and support better outcomes so you and your loved ones feel informed, heard, and empowered to heal. Because care isn&apos;t just about treatment — it&apos;s about making the connection.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {services.map((service) => (
          <ServiceTile key={service.id} service={service} />
        ))}
      </div>
    </div>
  </section>
);

const CtaSection = () => (
  <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
    <div className="max-w-4xl mx-auto text-center space-y-8">
      <h2 className="text-3xl sm:text-4xl font-bold">
        Ready to Experience Compassionate, Professional Care?
      </h2>
      <p className="text-xl text-blue-100">
        Book your consultation today and discover how our nurse companion services can bring peace of mind to you and your family.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/contact">
          <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-6 w-full sm:w-auto">
            Book Your Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

const Home = () => (
  <div className="min-h-screen">
    <PageMeta
      title="Bring-A-Nurse Senior Companion | Compassionate Nurse Companion Services"
      description="Bring-A-Nurse Senior Companion PLLC — compassionate, professional nurse companion services for seniors and their families in Fuquay Varina, Holly Springs, Apex, Cary, NC. Doctor visit support, advocacy, and healthcare navigation."
      path="/"
      schema={LOCAL_BUSINESS_SCHEMA}
    />
    <HeroSection />
    <ServicesSection />
    <AvailabilityChecker />
    <CtaSection />
  </div>
);

export default Home;
